import type { LoginResponse } from "@types_def/common/auth.types";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import axios from "axios";

import { isTokenExpiringSoon, storeAuthResult } from "../../utils/tokenManagement.util";

const BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
const ALB_API_KEY = import.meta.env.VITE_ALB_API_KEY;
const ALB_API_VALUE = import.meta.env.VITE_ALB_API_VALUE;

// Create a separate axios instance for refresh calls to avoid interceptor loops
const refreshAxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    [ALB_API_KEY]: ALB_API_VALUE,
  },
});

// Main axios instance with interceptors
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    [ALB_API_KEY]: ALB_API_VALUE,
  },
});

const rawApi: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    [ALB_API_KEY]: ALB_API_VALUE,
  },
});

// Refresh state management
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

function processQueue(error: any, token: string | null = null): void {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
}

// Refresh token function using the separate axios instance to avoid interceptor loops
async function performRefreshToken(): Promise<boolean> {
  const refreshTokenValue: string | null =
    typeof window !== "undefined" ? localStorage.getItem("RefreshToken") : null;

  if (!refreshTokenValue) {
    return false;
  }

  try {
    const res = await refreshAxiosInstance.post<
      { RefreshToken: string },
      AxiosResponse<LoginResponse>
    >("/auth/refresh-token", { RefreshToken: refreshTokenValue });

    if (res.data.ChallengeName === undefined) {
      storeAuthResult(res.data);
      return true;
    }

    return false;
  } catch (_e) {
    return false;
  }
}

// Request interceptor: Add auth token and proactively refresh if expiring
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const token = localStorage.getItem("AccessToken");

    // Check if token is expiring soon and refresh proactively
    if (token && isTokenExpiringSoon() && !isRefreshing) {
      isRefreshing = true;
      try {
        const success = await performRefreshToken();
        if (success) {
          const newToken = localStorage.getItem("AccessToken");
          config.headers = config.headers || {};
          config.headers.Authorization = newToken!;
        }
      } catch (_error) {
      } finally {
        isRefreshing = false;
      }
    } else if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = token;
      config.headers[ALB_API_KEY] = ALB_API_VALUE;
    }

    return config;
  },
  async (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

// Response interceptor: Handle 401 errors with token refresh
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 403 errors immediately
    if (error.response?.status === 403) {
      const errorData = error.response.data as { message?: string } | undefined;
      throw errorData?.message || error;
    }

    // Handle 401 unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request during refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(async (token) => {
            originalRequest.headers.Authorization = token as string;
            return api(originalRequest);
          })
          .catch(async (err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const success = await performRefreshToken();

        if (success) {
          const newToken = localStorage.getItem("AccessToken");
          processQueue(null, newToken);
          isRefreshing = false;

          originalRequest.headers.Authorization = newToken!;
          return await api(originalRequest);
        } else {
          throw new Error("Token refresh failed");
        }
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Clear auth and let the error propagate
        localStorage.removeItem("AccessToken");
        localStorage.removeItem("RefreshToken");

        // Only redirect if not already on login page
        const isOnLoginPage =
          window.location.pathname === "/login" || window.location.pathname === "/auth/login";
        if (!isOnLoginPage) {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Export the configured instance as default
export default api;

// Public refresh token function for external use (e.g., GraphQL client / graphql-fetcher)
export async function refreshToken(): Promise<boolean> {
  return performRefreshToken();
}

// Request without auth refresh/retry interceptors (login and account recovery).
export async function sendRawRequest<T, R>(
  method: "get" | "post" | "put" | "patch",
  url: string,
  data?: T,
  config?: AxiosRequestConfig<T>
) {
  const token = localStorage.getItem("AccessToken");
  const authConfig = {
    ...config,
    headers: {
      ...config?.headers,
      ...(token ? { Authorization: token } : {}),
      [ALB_API_KEY]: ALB_API_VALUE,
    },
  };

  if (method === "get") {
    return rawApi.get<T, AxiosResponse<R>>(url, authConfig);
  }

  return rawApi[method]<T, AxiosResponse<R>>(url, data, authConfig);
}

// Request with automatic retry via interceptors
export async function sendRequest<T, R>(
  method: "get" | "post" | "put" | "patch",
  url: string,
  data?: T,
  config?: AxiosRequestConfig<T>
) {
  const authConfig = {
    ...config,
    headers: {
      ...config?.headers,
      [ALB_API_KEY]: ALB_API_VALUE,
    },
  };
  if (method === "get") {
    return api.get<T, AxiosResponse<R>>(url, authConfig);
  }

  return api[method]<T, AxiosResponse<R>>(url, data, authConfig);
}
